<template>
    <div class="NotificationList">
        <AppTable
            ref="table"
            :columns="cols"
            :data="normalizeRows"
            :total="total"
            :loading="loading"
            :hideHeader="hideHeader"
            rowKey="uuid"
            class="Table"
            @table-request="handleFetchData"
        >
            <template #content="{ row }">
                <QTr>
                    <QTd
                        key="type"
                    >
                        <div class="text-body2 text-weight-medium">
                            {{ row.type }}
                        </div>
                    </QTd>
                    <QTd
                        key="title"
                        class="td-title"
                    >
                        <div class="text-body2 text-weight-medium">
                            {{ row.title }}
                        </div>
                        <div class="text-caption text-grey-7">
                            Created on {{ App.dateLocationFormat(row.createdAt, 'long') }}
                        </div>
                    </QTd>
                    <QTd
                        key="description"
                        class="td-desc"
                    >
                        <span class="td-desc ellipsis">{{ row.organization || '-' }}</span>
                        <QTooltip
                            v-if="row.organization"
                            maxWidth="300px"
                        >
                            {{ row.organization }}
                        </QTooltip>
                    </QTd>
                    <QTd
                        key="seenAt"
                        class="td-seenAt"
                    >
                        <span>{{ !!row.seenAt ? App.dateLocationFormat(row.seenAt, 'long') : '-' }}</span>
                    </QTd>
                    <QTd
                        key="content"
                        class="td-content"
                    >
                        <q-icon
                            name="description"
                            size="24px"
                            color="secondary"
                            clickable
                            style="cursor: pointer;"
                            @click="handleClick(row)"
                        />
                    </QTd>
                    <QTd
                        key="common"
                        class="td-common"
                    >
                        <div v-if="row.options">
                            <QBtn
                                flat
                                :disable="!!row.seenAt"
                                @click="handleEmmit(getOption(row), row)"
                            >
                                <q-icon
                                    :name="getOption(row).icon"
                                    size="24px"
                                />
                                <QTooltip
                                    v-if="!!getOption(row).label"
                                >
                                    {{ getOption(row).label }}
                                </QTooltip>
                            </QBtn>
                        </div>
                    </QTd>
                </QTr>
            </template>
            <template #empty>
                <EmptyList
                    :title="$t('notifications.notifications.empty.title')"
                    class="mt-2 mb-2"
                />
            </template>
        </AppTable>
    </div>
</template>

<script>
/* eslint-disable func-names */
import VueTypes from 'vue-types'
import { Table as AppTable, EmptyList } from '@/components'
import { filtersMixin } from '@/mixins'

const ACTIONS = {
    VISUALIZATION: 'VISUALIZATION',
}

export default {
    name: 'NotificationList',
    components: {
        AppTable,
        EmptyList,
    },
    mixins: [filtersMixin],
    inject: ['App'],
    props: {
        columns: VueTypes.array,
        rows: VueTypes.array,
        total: VueTypes.number,
        search: VueTypes.string,
        loading: VueTypes.bool.def(false),
    },
    emits: ['onFilterDate', 'onChangeVisibility', 'onFetchData', 'onClickContent'],
    data() {
        return {
            activateVisibility: false,
            filters: {
                startDate: '',
                endDate: '',
            },
        }
    },
    computed: {
        cols() {
            return [
                ...this.columns,
                {
                    name: 'type',
                    label: this.$t('general.type'),
                    align: 'left',
                    width: 100,
                },
                {
                    name: 'title',
                    label: this.$t('general.title'),
                    align: 'left',
                },
                {
                    name: 'organization',
                    label: this.$t('general.organization'),
                    align: 'left',
                },
                {
                    name: 'seenAt',
                    label: this.$t('general.seenAt'),
                    align: 'left',
                },
                {
                    name: 'content',
                    label: this.$t('general.content'),
                    align: 'left',
                },
                {
                    name: 'common',
                    label: this.$t('general.actions'),
                    align: 'center',
                },
            ]
        },
        hideHeader() {
            return !(this.rows || []).length
        },
        normalizeRows() {
            return (this.rows || []).map((row) => ({
                ...row,
                options: this.generateOptionsRow(row),
            }))
        },
    },
    methods: {
        handleSelectedDate() {
            const { filters } = this
            this.$emit('onFilterDate', filters)
        },
        generateOptionsRow() {
            return {
                alreadySeen: {
                    key: ACTIONS.VISUALIZATION, icon: 'visibility_off', iconColor: 'primary', disabled: true,
                },
                unseen: {
                    key: ACTIONS.VISUALIZATION, icon: 'visibility', iconColor: 'primary', label: 'Mark as seen', event: 'onVisualization',
                },
            }
        },
        handleVisibility() {
            this.activateVisibility = !this.activateVisibility
            this.resetDate()
            this.$emit('onChangeVisibility', { alreadySeen: this.activateVisibility })
        },
        handleEmmit({ event }, row) {
            this.$emit(event, row)
        },
        resetDate() {
            this.filters.startDate = ''
            this.filters.endDate = ''
        },
        getPagination() {
            return this.$refs.table.getPagination()
        },
        handleFetchData(props) {
            this.$emit('onFetchData', {
                ...props,
                alreadySeen: this.activateVisibility,
                ...(this.filters?.startDate ? { startDate: this.filters.startDate } : {}),
                ...(this.filters?.endDate ? { endDate: this.filters.endDate } : {}),
            })
        },
        getOption(row) {
            return row.seenAt ? row.options.alreadySeen : row.options.unseen
        },
        handleClick(row) {
            this.$emit('onClickContent', row)
        },
    },

}
</script>
<style scoped lang="scss">
.Table {
    position: relative;
    min-height: 135px;

    .Actions {
        position: sticky;
        right: 0;
        background: $table-bg;
    }

    .ellipsis {
        display: inline-block;
        vertical-align: middle;
        max-width: 300px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .td-common {
        text-align: center;
    }
}

.EmptyList {
    width: 100%;
}
</style>
